import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Table, InputNumber, Radio } from 'antd';
import { toast, Bounce } from 'react-toastify';
import { useSelector, useDispatch } from 'react-redux';
import { fetchCostCenterData } from 'store/slice/common';
import { Button } from 'components/Button';
import FilterSelect from 'components/Form/filterSelect';
import DateField from 'components/Form/dateField';
import EssFullWidthModal from 'components/modelpopup/baseModal';
import { useUnsavedChangesGuard } from 'context/unsavedChangesContext';
import { getIssuanceEmployeesApi, createIssuanceApi, getWarehouseStockApi, uniformErrorMessage } from 'api/services/uniformManagement';
import { useUniformMgmt } from '../../context/uniformMgmtContext';
import { employeeGenderOptions, getGenderLabel } from '../../constants';
import IssuanceErrorList from '../components/IssuanceErrorList';
import PositionUniformMappingModal from '../components/PositionUniformMappingModal';
import useStickyIdentity from '../../components/useStickyIdentity';

const toastOpts = { position: 'top-right', autoClose: 3000, transition: Bounce };

// Employee (CompanyUser.gender) values, not the uniform-ITEM M/F/U vocabulary
// — IssuanceEmployeesView filters CompanyUser.gender directly, so sending 'M'/
// 'F' here silently matched zero employees.
const genderChoices = employeeGenderOptions.map((g) => ({ label: g.label, value: g.value }));

/** Key an issuing-warehouse stock row (or a grid row's current size choice) the
 *  same way the backend's stock rows and `errors[]` rows are keyed, so
 *  availability lookups and server-error highlighting both agree on identity. */
const stockKey = (uniformItem, size) => `${uniformItem}-${size ?? 'none'}`;

/** Truncation for cells and panel fields whose content is unpredictable. Every
 *  use carries a `title` so the full value stays reachable on hover. */
const ELLIPSIS = { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };

/** "Same keys, same values" for the per-item size / quantity maps. Used to tell
 *  a grid the user has actually edited from one that is still showing exactly
 *  the presets applyEmployee installed. `?? null` so a missing key and an
 *  explicitly null one compare equal. */
const sameMap = (a, b) => {
  const keys = new Set([...Object.keys(a || {}), ...Object.keys(b || {})]);
  for (const key of keys) {
    if ((a?.[key] ?? null) !== (b?.[key] ?? null)) return false;
  }
  return true;
};

/** CASA-first single issuance: pick CASA (+ optional gender) to load eligible
 *  employees, pick one employee to load their position-mapped items grid,
 *  select rows, set a size (where the item has sizes) and quantity per row,
 *  then submit as one multi-line issuance from the chosen issuing warehouse. */
const IssuanceFormModal = ({ open, onClose, onIssued }) => {
  const { issuingWarehouseOptions, defaultIssuingWarehouseOption, warehousesLoading, warehousesError, fetchWarehouses,
    uniformPositionOptions, uniformPositionsLoading } = useUniformMgmt();
  const dispatch = useDispatch();
  const { costCenter, costCenterLoading, costCenterError } = useSelector((state) => state.common);

  const casaOptions = (costCenter || []).map((c) => ({ label: c.name || c.code, value: c.id, code: c.code || '' }));
  // Positions a uniform is mapped to, not every position in the company:
  // the rest return an empty grid when picked. Masters keeps the full list.
  const positionOptions = uniformPositionOptions;

  const [warehouse, setWarehouse] = useState(null);
  // CASA stays a SINGLE select here while Position is a multi, and the
  // asymmetry is deliberate. Both are scope filters over the eligible-employee
  // list, but the CASA is also recorded on the issuance this modal creates —
  // one employee, one cost centre — and the identity card below falls back to
  // `casa?.label` to name it. Position is never recorded from this control at
  // all, so widening it to "kitchen OR bar" only widens the employee dropdown,
  // which is exactly what a scope filter is for.
  const [casa, setCasa] = useState(null);
  const [position, setPosition] = useState([]);
  const [gender, setGender] = useState('');
  const [employees, setEmployees] = useState([]);
  const [employeesLoading, setEmployeesLoading] = useState(false);
  const [employee, setEmployee] = useState(null);

  const [showMappingModal, setShowMappingModal] = useState(false);
  // Bumped to re-run the employee fetch after inline position mappings are
  // created, so the item grid picks up the uniforms just mapped.
  const [employeesReloadToken, setEmployeesReloadToken] = useState(0);
  // Set immediately before such a reload: which employee to re-select (and
  // which item ids were just mapped) once the fresh list lands, so the user
  // carries straight on with the issuance that prompted the mapping. Null on
  // every other fetch — a CASA/position/gender change must NOT resurrect the
  // employee it deliberately cleared.
  const pendingReselectRef = useRef(null);

  const [sizeMap, setSizeMap] = useState({});
  // A row is part of the issuance purely by carrying a quantity above zero —
  // there is no separate tick step (mirrors TransferFormModal). Rows left at
  // zero are simply not issued.
  const [qtyMap, setQtyMap] = useState({});
  const [rowFieldErrors, setRowFieldErrors] = useState({});
  const [serverErrors, setServerErrors] = useState([]);

  const [stockMap, setStockMap] = useState({});
  const [stockLoading, setStockLoading] = useState(false);

  const [issuanceDate, setIssuanceDate] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // sizeMap is NOT usable as a dirty signal on its own: picking an employee
  // presets a size for every one of their mapped items (see applyEmployee), so
  // it is non-empty before the user has touched anything. qtyMap is preset too
  // — to zero on every row — so that an untouched grid is both clean AND
  // issues nothing. Keeping the presets lets the grid be compared by value
  // instead — so changing a size and changing it straight back reads as clean,
  // which a "was touched" flag could not express.
  const [presetSizeMap, setPresetSizeMap] = useState({});
  const [presetQtyMap, setPresetQtyMap] = useState({});

  // Reset the whole form whenever the modal is (re)opened.
  useEffect(() => {
    if (!open) return;
    setWarehouse(null);
    setCasa(null);
    setPosition([]);
    setGender('');
    setEmployees([]);
    setEmployee(null);
    setSizeMap({});
    setQtyMap({});
    setRowFieldErrors({});
    setServerErrors([]);
    setIssuanceDate('');
    setNotes('');
    setShowMappingModal(false);
    setPresetSizeMap({});
    setPresetQtyMap({});
    pendingReselectRef.current = null;
  }, [open]);

  // Auto-pick the issuing warehouse when there's only one to choose from —
  // runs after the reset effect above (which clears `warehouse` to null on
  // every open), so a single-option company never makes the user pick. Also
  // reconciles the other direction: if the picked warehouse drops out of the
  // list (deactivated / stripped of the role elsewhere), clear it rather than
  // leave a now-invalid warehouse selected and submittable.
  useEffect(() => {
    if (!open) return;
    if (defaultIssuingWarehouseOption) {
      setWarehouse((prev) => (
        prev && issuingWarehouseOptions.some((o) => o.value === prev.value)
          ? prev
          : defaultIssuingWarehouseOption
      ));
    } else if (warehouse && !issuingWarehouseOptions.some((o) => o.value === warehouse.value)) {
      setWarehouse(null);
    }
  }, [open, defaultIssuingWarehouseOption, issuingWarehouseOptions, warehouse]);

  // A stock check (validate/submit) that ran against one warehouse must not
  // survive a switch to another — otherwise row errors from A's 400 keep
  // rendering under B's grid, a false assurance the user could act on.
  useEffect(() => {
    setServerErrors([]);
    setRowFieldErrors({});
  }, [warehouse]);

  // Stock availability is scoped to whichever warehouse is currently chosen —
  // load its full stock so per-row availability is a local lookup. Guarded
  // with `ignore` so a slow response for a warehouse the user has since
  // switched away from (or the modal was closed/reopened mid-flight) can't
  // clobber a newer one and leave stale availability on screen. `stockMap` is
  // cleared synchronously before the fetch too, so the Avail column can never
  // read another warehouse's numbers while the new one is in flight.
  useEffect(() => {
    if (!open) return;
    if (!warehouse) { setStockMap({}); return; }
    let ignore = false;
    setStockLoading(true);
    setStockMap({});
    getWarehouseStockApi(warehouse.value, { page_size: 1000 }).then((response) => {
      if (ignore) return;
      const rows = response.data?.results || response.data?.data || [];
      const map = {};
      rows.forEach((r) => { map[stockKey(r.uniform_item, r.size)] = r.quantity || 0; });
      setStockMap(map);
    }).catch((e) => {
      if (ignore) return;
      setStockMap({});
      toast.error(uniformErrorMessage(e, 'Failed to load warehouse stock'), toastOpts);
    }).finally(() => {
      if (!ignore) setStockLoading(false);
    });
    return () => { ignore = true; };
  }, [open, warehouse]);

  // New employee -> rebuild the grid: preset each item's size, leave every
  // quantity at zero, and clear any errors from the previous employee (they
  // refer to a different set of items entirely).
  //
  // Quantity presets to 0, not 1. Quantity is now the only thing that puts a
  // row into the issuance, so a preset of 1 would mean "issue one of
  // everything this position maps" to anyone who picked an employee and hit
  // submit — and would also read as dirty-by-default under any rule that
  // treats a non-zero quantity as entered. Zero is the only preset that makes
  // an untouched grid both clean and inert.
  //
  // Size still presets: the profile size when the employee has one, otherwise
  // the item's only size when it has exactly one. A single-size item has no
  // decision to make, so making the user open a one-option picker on every
  // such row is pure friction. Profile size wins whenever it is present.
  // useCallback'd — it only calls setState, so it is stable — which lets the
  // fetch effect below reuse it for the post-mapping re-select without
  // tripping exhaustive-deps or re-running on every render.
  const applyEmployee = useCallback((val) => {
    setEmployee(val);
    const items = val?.items || [];
    const nextSize = {};
    const nextQty = {};
    items.forEach((it) => {
      const sizes = it.sizes || [];
      const soleSize = sizes.length === 1 ? sizes[0].id : null;
      nextSize[it.uniform_item] = it.profile_size ?? soleSize ?? null;
      nextQty[it.uniform_item] = 0;
    });
    setSizeMap(nextSize);
    setQtyMap(nextQty);
    setRowFieldErrors({});
    setServerErrors([]);
    // Remember exactly what was preset, so the dirty check can tell an untouched
    // grid from an edited one — a freshly picked employee is clean however many
    // rows it just filled in.
    setPresetSizeMap(nextSize);
    setPresetQtyMap(nextQty);
  }, []);

  // CASA (required), position or gender change -> reload eligible employees for
  // that scope. Guarded with `ignore` so a slower in-flight request for a scope
  // the user has since changed away from can't overwrite the list for the scope
  // now selected (e.g. switch CASA A -> B, A's response lands after B's and
  // would otherwise show A's staff while the CASA field reads B).
  useEffect(() => {
    if (!open || !casa) {
      pendingReselectRef.current = null;
      setEmployees([]);
      return undefined;
    }
    let ignore = false;
    setEmployeesLoading(true);
    const params = { cost_center: casa.value };
    // CSV, joined here rather than passed as an array: axios would serialize
    // an array to `position[]=1&position[]=2`, which this endpoint ignores.
    if (position.length) params.position = position.map((p) => p.value).join(',');
    if (gender) params.gender = gender;
    getIssuanceEmployeesApi(params).then((response) => {
      if (ignore) return;
      const rows = response.data?.data?.employees || [];
      const options = rows.map((e) => {
        // Kept separate from `label`: the label carries the emp_no in
        // parentheses so the dropdown is searchable by it, while the identity
        // card below renders the bare name with the ID on its own line.
        const name = e.name || `${e.first_name || ''} ${e.last_name || ''}`.trim();
        return {
        label: `${name} (${e.emp_no || '—'})`,
        value: e.company_user,
        name,
        emp_no: e.emp_no || '',
        gender: e.gender,
        position: e.position,
        position_name: e.position_name,
        cost_center_name: e.cost_center_name,
        items: e.items || [],
        };
      });
      setEmployees(options);

      // Only ever set by handleMappingSaved (cleared by every filter change),
      // so this can't resurrect an employee a CASA/position/gender switch
      // deliberately dropped.
      const reselect = pendingReselectRef.current;
      pendingReselectRef.current = null;
      if (!reselect) return;
      const match = options.find((o) => o.value === reselect.companyUser);
      if (!match) return;
      applyEmployee(match);
      // The API only lists items whose gender suits the employee, so a mapping
      // can be created correctly and still produce no row here. Say so rather
      // than leaving the same empty grid with no explanation.
      const created = reselect.createdItemIds || [];
      if (created.length > 0 && !match.items.some((it) => created.includes(it.uniform_item))) {
        toast.warning(
          `Mapped to ${match.position_name || 'the position'}, but none of the added uniforms suit this employee's gender, so none appear below.`,
          toastOpts
        );
      }
    }).catch((e) => {
      if (ignore) return;
      setEmployees([]);
      toast.error(uniformErrorMessage(e, 'Failed to load employees'), toastOpts);
    }).finally(() => {
      if (!ignore) setEmployeesLoading(false);
    });
    return () => { ignore = true; };
  }, [open, casa, position, gender, employeesReloadToken, applyEmployee]);

  // Any scope change drops the selected employee *and* their grid: the employee
  // may not exist in the new scope at all, and their rows/selection certainly
  // don't belong to it.
  const clearEmployeeSelection = () => {
    pendingReselectRef.current = null;
    setEmployee(null);
    setSizeMap({});
    setQtyMap({});
    setRowFieldErrors({});
    setServerErrors([]);
    setShowMappingModal(false);
    setPresetSizeMap({});
    setPresetQtyMap({});
  };

  const handleCasaChange = (val) => {
    setCasa(val);
    // Clearing the CASA disables the Position select without emptying it, so a
    // value left behind here would silently narrow the next CASA the user
    // picks — a filter they can neither see applied nor reach to clear.
    // Switching between CASAs keeps it: the control stays enabled and visibly
    // shows what it is filtering by.
    if (!val) setPosition([]);
    clearEmployeeSelection();
  };

  const handlePositionChange = (val) => {
    // `val || []` — react-select sends null, not [], when a multi is cleared
    // through its own clear button, and a null would break the `.length` reads
    // and re-run the fetch effect on a new identity every render.
    setPosition(val || []);
    clearEmployeeSelection();
  };

  const handleGenderChange = (e) => {
    setGender(e.target.value);
    clearEmployeeSelection();
  };

  const handleEmployeeChange = applyEmployee;

  // Fired by the inline mapping dialog whenever at least one mapping was
  // created — including a partial failure, where the dialog stays open on what
  // failed while these reloaded rows already reflect what landed.
  const handleMappingSaved = ({ createdItemIds }) => {
    if (employee) pendingReselectRef.current = { companyUser: employee.value, createdItemIds };
    setEmployeesReloadToken((t) => t + 1);
  };

  const clearRowErrors = (uniformItem) => {
    setRowFieldErrors((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((k) => { if (k.startsWith(`${uniformItem}-`)) delete next[k]; });
      return next;
    });
  };

  /** What the issuing warehouse holds of this item in this size. Availability is
   *  keyed per (item, size), so it moves whenever either does — which is why
   *  both update handlers below re-clamp through it rather than trusting the
   *  `max` on the input alone. 0 while the stock fetch is in flight or no
   *  warehouse is chosen; the Qty input is disabled in both those states so
   *  that zero is never mistaken for "out of stock". */
  const availableFor = (uniformItem, sizeId) => stockMap[stockKey(uniformItem, sizeId)] ?? 0;

  const updateSize = (uniformItem, sizeId) => {
    setSizeMap((prev) => ({ ...prev, [uniformItem]: sizeId }));
    // Changing size changes the cap. Without this, typing 8 against a size
    // with 10 in stock and then switching to a size with 2 leaves an
    // un-issuable 8 on the row — the `max` on the input having been bypassed
    // by editing a different control.
    //
    // Only ever called from the sized branch, so a null here means the user
    // CLEARED the size. Availability is unknowable in that state, and clamping
    // to the 0 that `stockKey(item, null)` misses on would silently wipe a
    // quantity they already typed. Leave it: the row prompts to choose a size
    // or clear the quantity, its Qty input stays enabled so the second option
    // is actually reachable, and the submit blocks until one of the two
    // happens — at which point this clamp runs properly.
    if (sizeId == null) { clearRowErrors(uniformItem); return; }
    const available = availableFor(uniformItem, sizeId);
    setQtyMap((prev) => (
      (prev[uniformItem] ?? 0) <= available ? prev : { ...prev, [uniformItem]: available }
    ));
    clearRowErrors(uniformItem);
  };

  const updateQty = (uniformItem, qty) => {
    // Clamped here rather than left to antd's own `max` handling: InputNumber
    // reports out-of-range values through onChange as they are typed or pasted
    // and only corrects them on blur, so without this the state briefly holds —
    // and a fast submit could send — a quantity above availability.
    const available = availableFor(uniformItem, sizeMap[uniformItem] ?? null);
    const next = Math.max(0, Math.min(Math.floor(qty || 0), available));
    setQtyMap((prev) => ({ ...prev, [uniformItem]: next }));
    clearRowErrors(uniformItem);
  };

  const itemRows = useMemo(() => employee?.items || [], [employee]);

  /** The rows actually being issued: those carrying a quantity. Single source of
   *  truth for validation, for the payload and for the submit button's state.
   *
   *  Derived by walking `itemRows`, NOT `Object.keys(qtyMap)`: uniform_item ids
   *  are numbers, and object keys come back as strings, so the latter would
   *  produce keys that fail every `r.uniform_item === key` lookup downstream.
   *  Walking the rows also guarantees each entered key still has a live row. */
  const enteredKeys = useMemo(
    () => itemRows.filter((it) => (qtyMap[it.uniform_item] ?? 0) > 0).map((it) => it.uniform_item),
    [itemRows, qtyMap]
  );

  const handleClose = () => {
    if (submitting) return;
    onClose();
  };

  // What "dirty" means for this form:
  //   gridEdited — a size or quantity that no longer matches what applyEmployee
  //     preset. This now covers the entered quantities too, which is the whole
  //     of the issuance: there is no separate tick step left to track.
  //     Compared by value against the presets rather than against an empty
  //     baseline, so a freshly picked employee — whose grid arrives with a size
  //     on every row — is clean however many rows it just filled in, while
  //     setting a size and putting it straight back also reads as clean.
  //     Quantities preset to 0, so an untouched grid has nothing entered and
  //     prompts nothing.
  //   issuanceDate / notes — typed.
  //   warehouse — excluded, it is auto-picked by the preselect effect whenever
  //     the company has a default issuing warehouse, so it is dirty on open
  //     through no action of the user's.
  //   casa / position / gender / employee — excluded. These are the scope
  //     filters that load the grid; each is a single dropdown pick that costs
  //     nothing to redo, and none of them is data the user authored. Note that
  //     changing any of them already discards the grid outright
  //     (clearEmployeeSelection), so they are not a stable thing to guard.
  const gridEdited = !sameMap(sizeMap, presetSizeMap) || !sameMap(qtyMap, presetQtyMap);

  const isDirty = gridEdited
    || !!issuanceDate
    || !!notes.trim();

  // `requestClose` drives the panel's X button and Esc, so those go through the
  // same prompt a sidebar click does instead of discarding silently.
  const { requestClose } = useUnsavedChangesGuard({
    active: open,
    isDirty,
    isBusy: submitting,
    onDiscard: handleClose,
    label: 'New Issuance',
  });

  const validate = () => {
    if (issuingWarehouseOptions.length === 0 && !warehousesLoading) {
      toast.error(
        warehousesError ? "Couldn't load warehouses — retry" : 'No issuing warehouse configured — flag one under Masters → Warehouses',
        toastOpts
      );
      return false;
    }
    if (!warehouse) { toast.error('Issue from warehouse is required', toastOpts); return false; }
    // Availability is read from `stockMap`, which is emptied synchronously
    // before every warehouse fetch. Validating against it mid-load would fail
    // every row with "Only 0 available" — a statement about the fetch, not the
    // stock. Submit is disabled in this state too; this is the backstop.
    if (stockLoading) { toast.error('Still loading stock for this warehouse — try again in a moment', toastOpts); return false; }
    if (!casa) { toast.error('Cost Center is required', toastOpts); return false; }
    if (!employee) { toast.error('Employee is required', toastOpts); return false; }
    if (!issuanceDate) { toast.error('Issuance date is required', toastOpts); return false; }
    if (enteredKeys.length === 0) { toast.error('Enter a quantity on at least one item', toastOpts); return false; }
    // A row carrying a quantity but missing a required size blocks the whole
    // submit rather than being dropped from the payload: the user asked for
    // that item, and silently issuing everything except it is the one outcome
    // they cannot see happening. The Issue Size cell already renders the same
    // condition inline (red border + "Size is required"), so the toast names
    // which item while the grid shows where.
    for (const key of enteredKeys) {
      const item = itemRows.find((r) => r.uniform_item === key);
      if (!item) continue;
      const hasSizes = (item.sizes || []).length > 0;
      if (hasSizes && !sizeMap[key]) {
        toast.error(`Choose a size for ${item.name}, or clear its quantity`, toastOpts);
        return false;
      }
      const available = availableFor(key, sizeMap[key] ?? null);
      if ((qtyMap[key] ?? 0) > available) {
        toast.error(`Only ${available} of ${item.name} available in this size`, toastOpts);
        return false;
      }
    }
    return true;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;

    const items = enteredKeys.map((key) => (
      { uniform_item: key, size: sizeMap[key] ?? null, quantity: qtyMap[key] }
    ));

    setSubmitting(true);
    setServerErrors([]);
    createIssuanceApi({
      warehouse: warehouse.value,
      company_user: employee.value,
      issuance_date: issuanceDate,
      notes: notes || '',
      items,
    }).then((response) => {
      setSubmitting(false);
      const reference = response.data?.data?.batch?.reference;
      toast.success(reference ? `Issued ${reference}` : 'Issuance created', toastOpts);
      onIssued();
    }).catch((err) => {
      setSubmitting(false);
      const rows = err.response?.data?.errors;
      if (Array.isArray(rows) && rows.length) {
        setServerErrors(rows);
        const nextFieldErrors = {};
        rows.forEach((row) => { nextFieldErrors[stockKey(row.uniform_item, row.size)] = row.error; });
        setRowFieldErrors(nextFieldErrors);
      }
      toast.error(uniformErrorMessage(err, 'Failed to create issuance'), toastOpts);
    });
  };

  const pinLeft = useStickyIdentity();

  const gridColumns = [
    {
      title: 'Uniform',
      dataIndex: 'name',
      // Bounded and truncating: this is the one column with unpredictable
      // content, and left to size itself a long item name pushed Issue Size
      // off the visible area — the "sizes are not showing" report. `minWidth: 0`
      // on the flex row is what lets the span actually shrink; without it a
      // flex item refuses to go below its content width and the ellipsis never
      // engages. Pinned too: the issuer types a size and a quantity at the far
      // right of a 770px grid inside a slide-over, and the item name is what
      // says which row those values land on.
      // `maxWidth`: the breakpoint reads the window, but this grid lives in a
      // slide-over that gives back ~225px to the sidebar, so the lane is capped
      // against the container it is actually in rather than the viewport.
      ...pinLeft(220, { maxWidth: 200 }),
      render: (name, item) => (
        <div className="d-flex align-items-center gap-2" style={{ minWidth: 0 }}>
          {item.image && (
            <img src={item.image} alt="" style={{ width: 28, height: 28, objectFit: 'cover', borderRadius: 4, flexShrink: 0 }} />
          )}
          <span title={name} style={ELLIPSIS}>{name}</span>
        </div>
      ),
    },
    {
      title: 'Profile Size',
      width: 110,
      render: (_, item) => item.profile_size_code || '—',
    },
    {
      // Widest of the editable columns and deliberately so: it is the one the
      // user has to reach on every row, so the read-only columns above yield
      // to it rather than the other way round.
      title: 'Issue Size',
      width: 200,
      render: (_, item) => {
        const hasSizes = (item.sizes || []).length > 0;
        // "Entered", not "ticked": a quantity is what puts this row in the
        // issuance, so a quantity is what makes a missing size an error.
        const entered = (qtyMap[item.uniform_item] ?? 0) > 0;
        if (!hasSizes) {
          // Sizeless items are always keyed with size=null — same composite key a
          // server errors[] row for this item would carry, so a stock-shortage
          // (etc.) error on a "Standard" row still highlights here.
          const standardErrKey = stockKey(item.uniform_item, null);
          const standardHasError = !!rowFieldErrors[standardErrKey];
          return (
            <div style={{ width: '100%', minWidth: 130 }}>
              <input className="form-control form-control-sm" value="Standard" disabled readOnly
                style={standardHasError ? { border: '1px solid #ff4d4f' } : undefined} />
              {standardHasError && <small className="text-danger d-block">{rowFieldErrors[standardErrKey]}</small>}
            </div>
          );
        }
        const sizeOptions = item.sizes.map((s) => ({ label: `${s.name} (${s.code})`, value: s.id }));
        const currentSizeId = sizeMap[item.uniform_item] ?? null;
        const currentSizeOpt = sizeOptions.find((o) => o.value === currentSizeId) || null;
        const missing = entered && !currentSizeId;
        const errKey = stockKey(item.uniform_item, currentSizeId);
        const selectId = `issue-size-${item.uniform_item}`;
        const missingMsgId = `${selectId}-missing`;
        const errorMsgId = `${selectId}-error`;
        const fieldError = rowFieldErrors[errKey];
        // The <label> now carries only the accessible NAME. The error text used
        // to be appended to it, because FilterSelect passed no aria-* through;
        // it takes `aria-describedby`/`aria-invalid` now, so the errors are
        // announced as a description pointing at the very <small> elements the
        // sighted user reads. Same words, same DOM nodes, one source — and the
        // name no longer changes as the row goes in and out of error.
        const selectLabel = `Issue size for ${item.name}`;
        const describedBy = [
          missing ? missingMsgId : null,
          fieldError ? errorMsgId : null,
        ].filter(Boolean).join(' ') || undefined;
        return (
          <div style={{ width: '100%', minWidth: 150 }}>
            <label className="visually-hidden" htmlFor={selectId}>{selectLabel}</label>
            <div style={missing ? { border: '1px solid #ff4d4f', borderRadius: 6 } : undefined}>
              <FilterSelect
                inputId={selectId}
                aria-describedby={describedBy}
                aria-invalid={missing || fieldError ? true : undefined}
                isClearable
                onChange={(opt) => updateSize(item.uniform_item, opt?.value ?? null)}
                optionLabel="label" optionValue="value"
                options={sizeOptions} value={currentSizeOpt}
                placeholder="Select size"
                noOptionsLabel="sizes"
              />
            </div>
            {/* Not "Size is required": the quantity is the other half of the
                problem and the half the user may prefer to remove, so the
                message names both ways out. */}
            {missing && <small id={missingMsgId} className="text-danger d-block">Choose a size or clear this quantity</small>}
            {fieldError && <small id={errorMsgId} className="text-danger d-block">{fieldError}</small>}
          </div>
        );
      },
    },
    {
      title: 'Qty',
      width: 130,
      render: (_, item) => {
        const sizeId = sizeMap[item.uniform_item] ?? null;
        const hasSizes = (item.sizes || []).length > 0;
        const available = availableFor(item.uniform_item, sizeId);
        const qty = qtyMap[item.uniform_item] ?? 0;
        // Four genuinely different reasons the input can be unusable, and only
        // the last is actually about this item's stock. stockMap is empty while
        // loading and with no warehouse, and is keyed per (item, size) — so a
        // sized row with no size chosen misses it too. Reporting any of those
        // three as "out of stock" would be a lie the user cannot act on.
        let blockedReason = null;
        if (!warehouse) blockedReason = 'Pick a warehouse';
        else if (stockLoading) blockedReason = 'Checking stock…';
        else if (hasSizes && sizeId == null) blockedReason = 'Select a size';
        else if (available <= 0) blockedReason = 'None in stock';

        // Never strand a quantity behind a disabled input. Clearing a size (or
        // stock dropping to 0 under an entered row) used to leave a quantity
        // that blocked the submit and could not be reached to remove — the only
        // way out was re-picking a size. A row the user has typed into stays
        // editable whenever the block is one they have to resolve themselves,
        // so it can always be zeroed.
        //
        // The two transient states stay disabled regardless: the value is
        // preserved untouched, submit is blocked while stock loads anyway, and
        // leaving them editable would let a mid-load edit clamp against an
        // empty stockMap.
        const transient = !warehouse || stockLoading;
        const disabled = !!blockedReason && (transient || qty === 0);
        const helpId = `qty-help-${item.uniform_item}`;
        return (
          <div style={{ minWidth: 0 }}>
            <InputNumber
              min={0}
              // The cap the backend would enforce anyway, applied where the
              // user can see it. updateQty re-clamps on top of this, because
              // InputNumber reports typed/pasted out-of-range values through
              // onChange and only corrects them on blur.
              //
              // Floored at the current quantity so antd's own blur-correction
              // can never silently rewrite a value the user already entered —
              // on a stranded row the cap is 0, and without this the first blur
              // would wipe the number instead of letting them decide.
              max={Math.max(available, qty)}
              // Blank rather than 0: an empty box reads as "not issuing this",
              // which is exactly what a zero quantity now means.
              value={qty > 0 ? qty : null}
              placeholder="0"
              disabled={disabled}
              aria-label={`Quantity to issue of ${item.name}`}
              aria-invalid={qty > available || undefined}
              aria-describedby={blockedReason ? helpId : undefined}
              onChange={(val) => updateQty(item.uniform_item, val)}
              // Fills its column instead of a fixed 80px, so the stepper tracks
              // the column rather than leaving dead space or overflowing it.
              style={{ width: '100%', minWidth: 64 }}
            />
            {blockedReason && (
              <small id={helpId}
                className={disabled ? 'text-muted d-block' : 'text-danger d-block'}
                style={{ fontSize: 11 }}>
                {/* An enabled row showing a reason is a stranded one: say what
                    to do about it rather than restating the state. */}
                {disabled ? blockedReason : `${blockedReason} — clear this quantity`}
              </small>
            )}
          </div>
        );
      },
    },
    {
      title: 'Available',
      width: 110,
      render: (_, item) => {
        const sizeId = sizeMap[item.uniform_item] ?? null;
        const available = availableFor(item.uniform_item, sizeId);
        // Quantities are clamped to availability on entry, so this can only
        // turn red when stock moved under a quantity already typed — still
        // worth flagging, since submit will now block on it.
        const exceeds = (qtyMap[item.uniform_item] ?? 0) > available;
        return <span className={exceeds ? 'text-danger fw-semibold' : ''}>{available}</span>;
      },
    },
  ];

  // `stockLoading` included: every row's availability comes from `stockMap`,
  // which is empty while a warehouse's stock is in flight, so a submit landing
  // in that window would be validated against nothing.
  const submitDisabled = submitting || stockLoading || !warehouse || !casa || !employee || enteredKeys.length === 0;

  return (
    <EssFullWidthModal mask={false} sidebarNavigable title="New Issuance" open={open}
      onCancel={requestClose} className="full-width-modal">
      <div className="modal-content">
        <div className="modal-body">
          <form onSubmit={handleSubmit}>
            <IssuanceErrorList errors={serverErrors} />

            <div className="row">
              <div className="col-12 col-sm-6 col-xl-3">
                <div className="input-block mb-3">
                  <label className="col-form-label">Issue From Warehouse <span className="text-danger">*</span></label>
                  {issuingWarehouseOptions.length === 0 && !warehousesLoading ? (
                    <div className="text-danger" style={{ fontSize: 12.5, padding: '8px 0' }}>
                      {warehousesError ? (
                        <>
                          Couldn't load warehouses —{' '}
                          <button type="button" className="btn btn-link p-0 align-baseline" onClick={fetchWarehouses}>
                            retry
                          </button>.
                        </>
                      ) : (
                        'No issuing warehouse configured — flag one under Masters → Warehouses.'
                      )}
                    </div>
                  ) : (
                    <FilterSelect onChange={setWarehouse} optionLabel="label" optionValue="value"
                      options={issuingWarehouseOptions} value={warehouse} placeholder="Select warehouse"
                      isLoading={warehousesLoading} noOptionsLabel="warehouses" />
                  )}
                </div>
              </div>
              <div className="col-12 col-sm-6 col-xl-3">
                <div className="input-block mb-3">
                  <label className="col-form-label">Cost Center <span className="text-danger">*</span></label>
                  {/* Same in-field treatment as the warehouse field above: CASA is
                      required and gates Position, Employee and submit, so if its list
                      failed to load the form is dead until it is retried. */}
                  {costCenterError && !costCenterLoading ? (
                    <div className="text-danger" style={{ fontSize: 12.5, padding: '8px 0' }}>
                      Couldn't load cost centers —{' '}
                      <button type="button" className="btn btn-link p-0 align-baseline"
                        onClick={() => dispatch(fetchCostCenterData())}>
                        retry
                      </button>.
                    </div>
                  ) : (
                    <FilterSelect isClearable onChange={handleCasaChange} optionLabel="label" optionValue="value"
                      options={casaOptions} value={casa} placeholder="Select Cost Center" isLoading={costCenterLoading}
                      noOptionsLabel="cost centers" />
                  )}
                </div>
              </div>
              <div className="col-12 col-sm-6 col-xl-3">
                <div className="input-block mb-3">
                  <label className="col-form-label">Position</label>
                  <FilterSelect isMulti checkboxMenu selectAll maxVisibleValues={2} isClearable
                    onChange={handlePositionChange} optionLabel="label" optionValue="value"
                    options={positionOptions} value={position} placeholder="All positions"
                    isLoading={uniformPositionsLoading} isDisabled={!casa} noOptionsLabel="positions" />
                </div>
              </div>
              <div className="col-12 col-sm-6 col-xl-3">
                <div className="input-block mb-3">
                  <label className="col-form-label d-block">Gender</label>
                  <Radio.Group value={gender} onChange={handleGenderChange}
                    optionType="button" buttonStyle="solid">
                    {genderChoices.map((g) => (
                      <Radio.Button key={g.value || 'all'} value={g.value}>{g.label}</Radio.Button>
                    ))}
                  </Radio.Group>
                </div>
              </div>
            </div>

            <div className="row">
              <div className="col-12 col-lg-6">
                <div className="input-block mb-3">
                  <label className="col-form-label">Employee <span className="text-danger">*</span></label>
                  <FilterSelect isClearable onChange={handleEmployeeChange} optionLabel="label" optionValue="value"
                    options={employees} value={employee}
                    placeholder={casa ? 'Select Employee' : 'Select a Cost Center first'}
                    isDisabled={!casa} isLoading={employeesLoading} noOptionsLabel="employees" />
                  {casa && !employeesLoading && employees.length === 0 && (
                    <small className="text-warning d-block">No eligible employees for this cost center/position/gender.</small>
                  )}
                </div>
              </div>
              {employee && (
                <div className="col-12 col-lg-6">
                  {/* The identity card for the person being issued to. It was
                      two loose stat blocks on a grey slab, which read as a pair
                      of orphaned fields rather than "this is who you are
                      issuing to" — the name, the one thing that confirms the
                      right person is selected, wasn't on it at all.
                      Restructured into a single card: the name leads, its ID
                      sits beneath it, and the three attributes follow as a
                      compact label/value strip that wraps instead of
                      overflowing. Brand red is used only as a 3px spine, so the
                      card registers as part of the module without competing
                      with the form controls beside it.
                      mt-lg-4 replaces a hardcoded marginTop:30, which existed
                      only to clear the Employee label on the same line. Below
                      lg this column stacks under that field, where the offset
                      was just a stray gap. */}
                  <div
                    className="d-flex flex-wrap align-items-center h-100 mt-0 mt-lg-4"
                    style={{
                      gap: '10px 22px',
                      background: '#fff',
                      border: '1px solid #F1F2F4',
                      borderLeft: '3px solid #D3001A',
                      borderRadius: 8,
                      padding: '10px 14px',
                    }}
                  >
                    <div style={{ flex: '1 1 150px', minWidth: 0 }}>
                      <div title={employee.name || undefined}
                        style={{ fontSize: 14.5, fontWeight: 600, color: '#1F1F1F', lineHeight: 1.35, ...ELLIPSIS }}>
                        {employee.name || '—'}
                      </div>
                      <div style={{ fontSize: 12, color: '#6B7280', letterSpacing: 0.2 }}>
                        {employee.emp_no || '—'}
                      </div>
                    </div>
                    <div className="d-flex flex-wrap" style={{ gap: '10px 20px', minWidth: 0 }}>
                      {[
                        { label: 'Position', value: employee.position_name || '—' },
                        { label: 'Cost Center', value: employee.cost_center_name || casa?.label || '—' },
                        { label: 'Gender', value: getGenderLabel(employee.gender) || '—' },
                      ].map((field) => (
                        <div key={field.label} style={{ minWidth: 0 }}>
                          <div style={{
                            fontSize: 10, fontWeight: 600, letterSpacing: 0.6,
                            textTransform: 'uppercase', color: '#6B7280', lineHeight: 1.6,
                          }}>
                            {field.label}
                          </div>
                          <div title={field.value}
                            style={{ fontSize: 13, fontWeight: 500, color: '#1F1F1F', ...ELLIPSIS }}>
                            {field.value}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="row">
              <div className="col-12 col-sm-5 col-xl-4">
                <div className="input-block mb-3">
                  <label className="col-form-label" htmlFor="issuance-date">Issuance Date <span className="text-danger">*</span></label>
                  <DateField id="issuance-date" value={issuanceDate} onChange={setIssuanceDate}
                    placeholder="Select issuance date" />
                </div>
              </div>
              <div className="col-12 col-sm-7 col-xl-8">
                <div className="input-block mb-3">
                  <label className="col-form-label">Notes</label>
                  <input className="form-control" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Notes" />
                </div>
              </div>
            </div>

            <hr />
            <h5 className="mb-2" style={{ fontSize: 15 }}>Items</h5>

            {!employee ? (
              <div className="text-muted py-4 text-center">Select an employee to see their mapped uniform items.</div>
            ) : itemRows.length === 0 ? (
              <div className="py-4 text-center">
                {employee.position ? (
                  <>
                    {/* Deliberately not "no uniforms mapped": the API also drops
                        mapped items whose gender doesn't suit the employee, so
                        an empty grid doesn't prove the position is unmapped. */}
                    <div className="text-muted mb-2">
                      No uniforms are available for {employee.position_name || 'this position'}
                      {employee.gender ? ` and ${getGenderLabel(employee.gender).toLowerCase()} employees` : ''}.
                    </div>
                    <Button type="button" icon="fa fa-plus" className="btn btn-outline-primary btn-sm"
                      label={`Assign uniforms to ${employee.position_name || 'this position'}`}
                      onClick={() => setShowMappingModal(true)} />
                    <div className="text-muted mt-2" style={{ fontSize: 12 }}>
                      Mapping is master data — it applies to every employee in this position.
                    </div>
                  </>
                ) : (
                  <div className="text-muted">
                    This employee has no position assigned, so no uniforms can be mapped to them —
                    set their position under Employees first.
                  </div>
                )}
              </div>
            ) : (
              <Table
                rowKey={(r) => r.uniform_item}
                columns={gridColumns}
                dataSource={itemRows}
                loading={stockLoading}
                pagination={false}
                size="small"
                // `tableLayout` is what makes the per-column widths above
                // binding. rc-table 7.54.0 (es/Table.js:446-463) picks the
                // layout in this order: an explicit `tableLayout` wins; then a
                // `fixed` column; then `fixHeader` (i.e. a `scroll.y`),
                // `isSticky`, or any column with `ellipsis`; otherwise 'auto'.
                // A numeric `scroll.x` is NOT in that list — it only changes
                // the answer inside the fixed-column branch. This table has no
                // fixed column, no scroll.y, no sticky header and no ellipsis
                // column, so without this prop it stays `table-layout: auto`
                // and the widths are hints the browser may exceed — on exactly
                // the grid the "sizes not showing" report was filed against.
                // Set explicitly rather than by bolting on a trigger, so the
                // intent survives someone later removing an ellipsis flag.
                tableLayout="fixed"
                // 770 is the sum of the five column widths. The selection
                // gutter that used to be in this total is gone with the
                // checkbox column; Qty took some of it back (96 -> 130) to
                // carry the disabled-reason line under the stepper.
                scroll={{ x: 770 }}
              />
            )}

            <div className="submit-section">
              <Button loading={submitting} disabled={submitDisabled} className="btn btn-primary submit-btn"
                type="submit" label="Issue Uniform" />
            </div>
          </form>

          {/* Renders through a portal, so it is not nested inside the form
              above — no stray submits from its controls. */}
          <PositionUniformMappingModal
            open={showMappingModal}
            position={employee?.position ? { id: employee.position, name: employee.position_name } : null}
            onClose={() => setShowMappingModal(false)}
            onSaved={handleMappingSaved}
          />
        </div>
      </div>
    </EssFullWidthModal>
  );
};

export default IssuanceFormModal;
